/**
 * services/BrokerService.ts
 * Service for managing broker entities with fee configuration access
 *
 * Brokers represent trading platform connections with embedded fee structures.
 * Extends BaseService to provide CRUD operations with status-based filtering
 * and fee configuration queries.
 */

import { BaseService } from './BaseService';
import type { Broker } from '@/types/schema/Broker';
import type { EntityStatus } from '@/types/shared/EntityStatus';
import { isEntityActive } from '@/types/shared/EntityStatus';

/**
 * Service for managing broker entities
 *
 * Provides CRUD operations plus status-based filtering and fee configuration access.
 * Inherits getAll(), get(), update(), softDelete() from BaseService.
 */
export class BrokerService extends BaseService<Broker> {
  constructor() {
    super('brokers');
  }

  /**
   * Add a new broker with default values
   *
   * Defaults to status: 'active' and empty feeConfigurations array if not provided.
   *
   * @param data - Partial broker data
   * @returns Promise resolving to the created broker
   */
  override async add(data: Partial<Broker>): Promise<Broker> {
    return super.add({ status: 'active', feeConfigurations: [], ...data });
  }

  /**
   * Get brokers by status
   *
   * Filters brokers by the specified EntityStatus.
   * Uses getAll() to ensure tombstoned records are excluded.
   *
   * @param status - The status to filter by ('active' | 'inactive' | 'archived')
   * @returns Promise resolving to array of brokers with matching status
   */
  async getByStatus(status: EntityStatus): Promise<Broker[]> {
    const allBrokers = await this.getAll();
    return allBrokers.filter(broker => broker.status === status);
  }

  /**
   * Get all active brokers
   *
   * Convenience method for getting brokers where status is 'active'.
   * Uses getAll() to ensure tombstoned records are excluded.
   *
   * @returns Promise resolving to array of active brokers
   */
  async getActiveBrokers(): Promise<Broker[]> {
    const allBrokers = await this.getAll();
    return allBrokers.filter(broker => isEntityActive(broker.status));
  }

  /**
   * Get broker with its fee configurations
   *
   * Returns the broker entity which includes embedded feeConfigurations array.
   * Returns undefined if broker not found.
   *
   * @param id - Broker ID
   * @returns Promise resolving to broker with fee configurations, or undefined
   */
  async getBrokerWithFeeConfigurations(id: string): Promise<Broker | undefined> {
    return this.get(id);
  }
}

// Export singleton instance
export const brokerService = new BrokerService();
